<script setup lang="ts">
import type { ContentfulClientApi } from 'contentful'
import { TabsBlock, Section, Button, TestimonialBlock, IntroBlock } from 'refresh-ui'
import { documentToHtmlString } from '@contentful/rich-text-html-renderer'
import type { CMSSettings, CloudinaryMedia } from '~/types/Contentful'

const { signIn, getSession } = useAuth()
import { joinURL } from 'ufo'
import { cloudinaryBuildImageURL } from '~/helpers/cloudinary'
import { usePageMeta } from '~/composables/usePageMeta'
import type { BankIdAuth } from '~/components/FormBankid.vue'

definePageMeta({
  layout: 'login',
})
const router = useRouter()
const { $contentful } = useNuxtApp()
const contentful = $contentful as ContentfulClientApi<undefined>
const cloudName = useRuntimeConfig().public.CLOUDNAME as string

const authenticated = ref<BankIdAuth>({ bankId: false, ssn: '', name: { first: '', last: '' } })
const isSigningIn = ref(false)

const ICON_MAP: Record<string, { icon: any; iconClass: string }> = {
  flash: { icon: 'svgo-navbar-flash', iconClass: 'h-[20px] w-[15px]' },
  'logo-heart': { icon: 'svgo-navbar-dr-heart', iconClass: 'h-[20px] w-[32px]' },
  badge: { icon: 'svgo-navbar-badge', iconClass: 'h-[20px] w-[22px]' },
  profile: { icon: 'svgo-navbar-profile', iconClass: 'h-[20px] w-[18px]' },
}

const getIconData = (iconType: string) => ICON_MAP[iconType] ?? ''

const { data } = useAsyncData('settings', async () => {
  try {
    const content = (await contentful.getEntries({
      content_type: 'settings',
      'sys.id': '5O66YhH304DcDr1WUYhwEP',
      limit: 1,
    })) as unknown as CMSSettings

    if (content.items.length > 0) {
      const entry = content?.items[0]?.fields
      const tabs = entry?.tabs
      const testimonials = entry?.testimonials
      const introBlock = entry?.loginPageIntroBlock?.fields

      const dynamicTabs =
        tabs?.map(tab => {
          const { icon, iconClass } = getIconData(tab?.fields?.iconType || '')
          return {
            tabLabel: tab?.fields?.tabLabel || '',
            title: tab?.fields?.title || '',
            copy: documentToHtmlString(tab?.fields?.copy),
            icon,
            iconClass,
          }
        }) ?? undefined

      const tabsData = {
        type: 'paragraph--content_tabs',
        id: 'login-content-tabs',
        title: introBlock?.title || '',
        subtitle: introBlock?.subtitle || '',
        copy: documentToHtmlString(introBlock?.copy),
        image: {
          type: 'image',
          url: 'https://res.cloudinary.com/' + cloudName + '/image/upload/v1678455267/djurensratt-se/fokusomr%C3%A5den/grisar/grisar_30.jpg',
          alt: 'Välkommen till Djurens Rätts BlixtApp!',
        },
        tabs: dynamicTabs,
        options: {
          hideIntroMobile: true,
          NoTopSpaceMobile: true,
        },
      }

      // Process Testimonials
      const cards =
        testimonials?.map(testimonial => {
          const cloudinaryImage = testimonial?.fields?.cloudinaryImage ? testimonial?.fields?.cloudinaryImage as CloudinaryMedia : null
          const imageURL = cloudinaryImage ? cloudinaryBuildImageURL(cloudinaryImage[0]?.public_id, cloudName, 400, 400) : ''
          const imageTitle = cloudinaryImage ? cloudinaryImage[0]?.context?.custom?.caption : null
          return {
            title: testimonial?.fields?.title || '',
            name: testimonial?.fields?.name || '',
            position: testimonial?.fields?.position || '',
            provider: 'cloudinary',
            copy: documentToHtmlString(testimonial?.fields?.copy),
            date: testimonial?.fields?.date ?? '',
            image: {
              type: 'image',
              url: imageURL,
              alt: imageTitle ?? 'card-img',
            },
          }
        }) ?? undefined

      const testimonialData = {
        type: 'paragraph--content_testimonial',
        id: 'login-testimonial',
        cards: cards,
      }

      return { tabsData, testimonialData, loginMetaDescription: entry.loginMetaDescription }
    }
    return { tabsData: null, testimonialData: null }
  } catch (error) {
    console.error('Error fetching Contentful data:', error)
    throw error
  }
})

usePageMeta({
  title: 'Välkommen till BlixtAppen - Logga in och gör skillnad!',
  isPublicPage: true,
  description: () => data.value?.loginMetaDescription || '',
})

const handleSignIn = async (provider: string) => {
  if (isSigningIn.value) return
  isSigningIn.value = true
  try {
    await signIn(
      provider,
      {
        callbackUrl: joinURL(
          `${useRequestURL().protocol}//${useRequestURL().hostname}${useRequestURL().port === '80' || useRequestURL().port === '8080' ? '' : `:${useRequestURL().port}`}`,
          '/login',
        ),
      },
      { prompt: 'login' },
    )
  } finally {
    isSigningIn.value = false
  }
}

const handleSignUp = async (provider: string) => {
  if (isSigningIn.value) return
  isSigningIn.value = true
  try {
    await signIn(
      provider,
      {
        callbackUrl: joinURL(
          `${useRequestURL().protocol}//${useRequestURL().hostname}${useRequestURL().port === '80' || useRequestURL().port === '8080' ? '' : `:${useRequestURL().port}`}`,
          '/login',
        ),
      },
      { prompt: 'login', screen_hint: 'signup' },
    )
  } finally {
    isSigningIn.value = false
  }
}

const introBlock = {
  id: 'login-testimonial',
  title: 'Vad säger andra Blixtar?',
  subtitle: 'recensioner',
  position: 'center',
}

const handleBankIdLogin = async () => {
  try {
    const result = await signIn('credentials', {
      referenceToken: authenticated.value.referenceToken,
      redirect: false,
    })

    if (result?.error) {
      throw new Error(result.error)
    }
    await getSession()
    router.push({ path: router.currentRoute.value.path, force: true })
  } catch (e) {
    console.error('Login session creation failed', e)
  }
}
</script>

<template>
  <Section space="without-space" background="white">
    <TabsBlock ref="tabRef" :data="data?.tabsData" class="!pb-0" v-if="data?.tabsData"
      ><template v-slot:after-content
        ><div class="mx-4 pb-[50px] lg:mx-0 lg:pb-[77px]">
          <div class="mt-7 md:text-center lg:text-start">
            <!-- <p class="font-sans text-sm">Kom igång genom att</p>
            <h4 class="text-start text-xl font-semibold">Logga in eller skapa konto</h4>
            <div
              class="relative mb-8 mt-2 flex w-full items-center justify-center before:absolute before:block before:h-[1px] before:w-full before:bg-blue before:content-[''] lg:mt-10"
            ></div> -->
            <div
              class="lg: relative my-7 mb-8 flex w-full items-center justify-center before:absolute before:block before:h-[1px] before:w-full before:bg-blue before:content-[''] lg:mt-10"
            >
              <span class="relative z-10 bg-white px-3 text-lg font-semibold lg:text-[20px]"
                >Logga in eller skapa konto</span
              >
            </div>
            <FormBankid
              class="w-full"
              :type-is-id="true"
              :sum="0"
              @update:auth="authenticated = $event"
              @on-next="handleBankIdLogin"
            />
            <!-- <div
              class="relative my-7 flex w-full items-center justify-center before:absolute before:block before:h-[1px] before:w-full before:bg-blue before:content-[''] lg:mt-10"
            >
              <span class="relative z-10 bg-white px-3 text-lg lg:text-[20px]">eller</span>
            </div>
            <Button title="Skapa konto" class="mt-3.5 w-full" @click="handleSignUp('auth0')" />
            <Button
              title="Logga in"
              type="outline"
              class="mt-3.5 w-full"
              @click="handleSignIn('auth0')"
            /> -->
            <button
              class="mx-2 my-4 text-lg text-blue lg:mt-6 disabled:opacity-50"
              :disabled="isSigningIn"
              @click="handleSignUp('auth0')"
            >
              Använd en annan metod
            </button>
          </div>
          <!-- <div v-if="!useBankid" class="mt-7 text-center lg:w-[349px]">
            <Button title="Skapa konto" class="mt-3.5 w-full" @click="handleSignUp('auth0')" />
            <Button
              title="Logga in"
              type="outline"
              class="mt-3.5 w-full"
              @click="handleSignIn('auth0')"
            />
            <button class="mx-2 my-6 text-xl text-blue" @click="useBankid = !useBankid">
              Använd BankID
            </button>
          </div> -->
        </div></template
      >
    </TabsBlock>
  </Section>
  <Section background="blue-light" v-if="data?.testimonialData?.cards">
    <IntroBlock :data="introBlock" />
    <TestimonialBlock :data="data.testimonialData" class="!mt-7" />
  </Section>

  <!-- <QRModal :qr-img="qrCode" :show="showQRModal" @closeModal="handleCloseQRModal" /> -->
</template>
